import { apiService } from './apiService';
import { aiEndpoints } from '@/api/apiEndpoint';
import { DefaultResponse } from '@/common/types/common';

export interface AnalyzeBusinessRegistrationResponse {
  companyName: string;
  representative: string;
  businessNumber: string;
  corporateNumber: string;
  establishmentDate: string;
}

interface AiAnalysisRequest {
  companyId: number;
  files: {
    fileId: number,
    documentTypeId: number
  }[]
}

interface IsBusyResponse {
  isBusy: boolean;
}

interface AiAnalysisFinancialRequest {
  companyId: number;
  data: {
    file: number,
    year: number
  }[]
}

interface AiAnalysisFinancialSingleDocumentRequest {
  companyId: number;
  fileId: number;
}

interface AiAnalysisFinancialSingleDocumentResponse {
  year: number;
  quarter: number;
  // 재무제표 데이터
  currentAssets: number;
  quickAssets: number;
  inventoryAssets: number;
  otherCurrentAssets: number;
  
  nonCurrentAssets: number;
  investmentAssets: number;
  tangibleAssets: number;
  intangibleAssets: number;
  otherNonCurrentAssets: number;
  
  totalAssets: number;
  
  totalLiabilities: number;
  currentLiabilities: number;
  nonCurrentLiabilities: number;
  
  totalEquity: number;
  capital: number;
  capitalSurplus: number;
  capitalAdjustments: number;
  accumulatedOtherComprehensiveIncome: number;
  retainedEarnings: number;
  deficit: number;
  
  totalLiabilitiesAndEquity: number;
  
  // 손익계산서 데이터
  sales: number;
  salesProfit: number;
  costOfGoodsSold: number;
  operatingProfit: number;
  sellingGeneralAdminExpenses: number;
  profitBeforeTax: number;
  nonOperatingIncome: number;
  nonOperatingExpenses: number;
  incomeTaxExpense: number;
  netProfit: number;
}

interface FinancialAiRequest {
  companyId: number;
  portfolioId?: number;
  filePaths: string[];
}

interface FinancialAiResponse {
  success: boolean;
  data?: any;
  message?: string;
}

interface RequestFormSubmitRequest {
  reportId: number;
  portfolioId: number;
  content: any;
}

interface RequestFormSubmitResponse {
  success: boolean;
  message?: string;
  id?: number;
}

interface ShareholderAnalysisRequest {
  fileId: number;
  portfolioId?: number;
}

interface StockholderDto {
  index: number;
  name: string;
  residentNumber?: string;
  businessNumber?: string;
  relation?: string;
  address?: string;
  stockType?: string;
  stockNumber?: number;
  shareholdingRate?: number;
  firstAcquisitionDate?: string;
  lastAcquisitionDate?: string;
}

interface StockholderAnalysisDto {
  stockholders: StockholderDto[];
}

export const aiService = {
  async isBusy(): Promise<IsBusyResponse> {
    try {
      const response = await apiService.get<IsBusyResponse>(
        aiEndpoints.isBusy()
      )
      return response
    } catch (err: any) {
      console.error(err)
      return {
        isBusy: false
      }
    }
  },

  async analyzeShareholderList(
    fileId: number,
    portfolioId?: number
  ): Promise<StockholderAnalysisDto> {
    try {
      const response = await apiService.post<StockholderAnalysisDto>(
        aiEndpoints.analyzeShareholderList(),
        { fileId, portfolioId }
      )
      return response
    } catch (err: any) {
      console.error('Shareholder analysis error:', err)
      throw new Error(err.response?.data?.message || '주주명부 분석 중 오류가 발생했습니다.')
    }
  },


  async aiAnalysisFinancialDocuments(
    data: AiAnalysisFinancialRequest
  ): Promise<DefaultResponse> {
    try {
      const response = await apiService.post<DefaultResponse>(
        aiEndpoints.aiAnalysisFinancialDocuments(),
        data,
      )
      return response
    } catch (err: any) {
      console.error(err)
      return {
        success: false
      }
    }
  },

  // 새로운 investor 서버 API 메서드들
  async getCompanyFinancialAiResponse(
    data: FinancialAiRequest
  ): Promise<FinancialAiResponse> {
    try {
      // URL에 쿼리 파라미터로 전송 (GET 요청)
      const queryParams = new URLSearchParams();
      queryParams.append('companyId', data.companyId.toString());
      if (data.portfolioId) {
        queryParams.append('portfolioId', data.portfolioId.toString());
      }
      data.filePaths.forEach(path => {
        queryParams.append('filePaths', path);
      });

      const response = await apiService.get<FinancialAiResponse>(
        `${aiEndpoints.getCompanyFinancialAiResponse()}?${queryParams.toString()}`
      )
      return response
    } catch (err: any) {
      console.error('Financial AI analysis error:', err)
      return {
        success: false,
        message: err.response?.data?.message || '재무제표 분석 중 오류가 발생했습니다.'
      }
    }
  },

  getAnalyzeBusinessRegistration: async (
    data: {
      fileId: number;
      portfolioId?: number;
    }
  ): Promise<AnalyzeBusinessRegistrationResponse | null> => {
    try {
      const response = await apiService.post<AnalyzeBusinessRegistrationResponse>(
        aiEndpoints.getAnalyzeBusinessRegistration(),
        data
      )
      return response
    } catch (err: any) {
      console.error(err)
      return null
    }
  }
};

export type {
  AiAnalysisRequest,
  AiAnalysisFinancialRequest,
  AiAnalysisFinancialSingleDocumentRequest,
  AiAnalysisFinancialSingleDocumentResponse,
  IsBusyResponse,
  FinancialAiRequest,
  FinancialAiResponse,
  RequestFormSubmitRequest,
  RequestFormSubmitResponse,
  ShareholderAnalysisRequest,
  StockholderDto,
  StockholderAnalysisDto
};